import { Filter } from '@/interfaces/filter.interface'
import { APIPermission } from '@/models/api/permission.model'
import { APIRole } from '@/models/api/role.model'
import { APIUser } from '@/models/api/user.model'
import { UsersServices } from '@/services/modules/users.service'
import store from '@/store'
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'

@Module
export default class UsersStore extends VuexModule {
    users: object[] = []
    usersPaginate: object[] = []
    userForm: object|null = null
    userPermissions: object[] = []
    UserMenu: object[] = []

    @Mutation
    updateUsers (newUsers: object[]) {
        this.users = newUsers
    }

    @Mutation
    updateUsersPaginate (newUsers: object[]) {
        this.usersPaginate = newUsers
    }

    @Mutation
    updateUserForm (newUserForm: object) {
        this.userForm = newUserForm
    }

    @Mutation
    updateUserPermissions (permissionList: object[]) {
        this.userPermissions = permissionList
    }

    @Mutation
    updateUserMenu (newUserMenu: object[]) {
        this.UserMenu = newUserMenu
    }

    @Action
    getUsers () {
        return new Promise((resolve) => {
            UsersServices.getUsers()
                .then(resp => {
                    store.commit('updateUsers', resp.data)
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    getUsersPaginate (params: { page: number; filters?: Filter[]; limit: number; sortBy: string; sort: string }) {
        return new Promise((resolve) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const newParams: {[key: string]: any} = {
                page: params.page,
                limit: params.limit,
                sortBy: params.sortBy,
                sort: params.sort
            }
            if (params.filters) {
                params.filters.forEach((filter: Filter) => {
                    if (filter.queryValue) {
                        if (!newParams[filter.value]) {
                            newParams[filter.value] = filter.queryValue
                        } else {
                            let helper: string[] = []

                            if (Array.isArray(newParams[filter.value])) {
                                helper = [ ...newParams[filter.value] ]
                            } else {
                                helper.push(newParams[filter.value])
                            }

                            if (typeof filter.queryValue === 'string') helper.push(filter.queryValue)

                            newParams[filter.value] = helper
                        }
                    }
                })
            }

            UsersServices.getUsersPaginate(newParams)
                .then(resp => {
                    store.commit('updateUsersPaginate', resp.data)
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    getUserById (id: number) {
        return new Promise((resolve) => {
            UsersServices.getUserById(id)
                .then(resp => {
                    store.commit('updateUserForm', resp.data)
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    createUser (params: APIUser) {
        params = new APIUser(params)

        params.userEnabled = 1

        return new Promise((resolve) => {
            if (!(params instanceof APIUser)) {
                store.commit('reportError', { code: 1000, message: 'Something went wrong :(' })
                resolve()
                return
            }

            UsersServices.createUser(params)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    editUser (params: APIUser) {
        params = new APIUser(params)

        return new Promise((resolve) => {
            if (!(params instanceof APIUser)) {
                store.commit('reportError', { code: 1000, message: 'Something went wrong :(' })
                resolve()
                return
            }

            UsersServices.editUser(params)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    deleteUsers (id: number) {
        return new Promise((resolve) => {
            UsersServices.deleteUser(id)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    resetPassword (params: { userId: number; password: string }) {
        return new Promise((resolve) => {
            UsersServices.resetPassword(params)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    addRoleToUser ({ userId, roles }: { userId: number; roles: APIRole[] }) {
        return new Promise((resolve) => {
            const roleList: number[] = []

            roles.forEach(role => {
                if (role.roleId) roleList.push(role.roleId)
            })

            UsersServices.addRoleToUser(userId, roleList)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    removeRoleToUser ({ userId, roles }: { userId: number; roles: APIRole[] }) {
        return new Promise((resolve) => {
            const roleList: number[] = []

            roles.forEach(role => {
                if (role.roleId) roleList.push(role.roleId)
            })

            UsersServices.removeRoleToUser(userId, roleList)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    addPermimissionToUser ({ userId, permissions }: { userId: number; permissions: APIPermission[] }) {
        return new Promise((resolve) => {
            const permissionList: number[] = []

            permissions.forEach(permission => {
                if (permission.permissionId) permissionList.push(permission.permissionId)
            })

            UsersServices.addPermissionToUser(userId, permissionList)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    removePermissionToUser ({ userId, permissions }: { userId: number; permissions: APIPermission[] }) {
        return new Promise((resolve) => {
            const permissionList: number[] = []

            permissions.forEach(permission => {
                if (permission.permissionId) permissionList.push(permission.permissionId)
            })

            UsersServices.removePermissionToUser(userId, permissionList)
                .then(resp => {
                    resolve(resp)
                })
                .catch(error => {
                    resolve(error.response)
                })
        })
    }

    @Action
    getUserLogued () {
        return new Promise(resolve => {
            if (!store.state.AuthStore?.auth?.user?.userId) {
                console.error('Missing user id [getPermissionsFromCurrentUser]')
                resolve()
            }

            UsersServices.getUserLogued()
                .then(resp => {
                    if (resp.status === 200) {
                        store.commit('updateUserPermissions', resp.data.permissions)
                        localStorage.setItem('permissions', JSON.stringify(resp.data.permissions))

                        const newAuth = JSON.parse(JSON.stringify(store.state.AuthStore.auth))
                        newAuth.user = resp.data
                        store.commit('updateAuth', newAuth)
                        resolve(newAuth)
                    }
                })
                .catch(error => {
                    resolve(error)
                })
        })
    }

    @Action
    getMenuUser () {
        return new Promise(resolve => {
            if (!store.state.AuthStore?.auth?.user?.userId) {
                store.commit('reportError', { code: 1000, message: 'Could not get menu from user :(' })
                resolve()
            }

            UsersServices.getMenuUser(store.state.AuthStore.auth.user.userId)
                .then(resp => {
                    if (resp.status === 200) {
                        store.commit('updateUserMenu', resp.data)
                        resolve(resp)
                    }
                })
                .catch(error => {
                    resolve(error)
                })
        })
    }
}
